import { PermissionActionEnum, PermissionEntityEnum } from "@/graphql/generated/graphql";
import { SpeedDial, SpeedDialIcon, SpeedDialAction } from "@mui/material";
import { map } from "lodash";
import RequestAccessBlock from "../RequestPermissionTooltip/RequestAccessBlock";
import { EditNote, FlashOn, NoteAdd } from "@mui/icons-material";
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import AddBoxIcon from '@mui/icons-material/AddBox';
import VideoChatIcon from '@mui/icons-material/VideoChat';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import PermissionContext from "@/Contexts/Permissions";
import React, { useContext, useState } from "react";
import { useReactiveVar } from "@apollo/client/react";
import { ModalType, setOpenedModals } from "@/graphql/reactiveVars";
import ControlledModals from "./ControlledModals";

interface SpeedDialActionItem {
  icon: React.ReactNode;
  name: string;
  permissibleEntity: PermissionEntityEnum | null;
  modal: ModalType;
  hideSpiderOnClick?: boolean;
}

const speedDialActions: SpeedDialActionItem[] = [
  { icon: <EditNote />, name: 'Open Notes', permissibleEntity: null, modal: 'Note' },
  { icon: <NoteAdd />, name: 'Create Note', permissibleEntity: null, modal: 'CreateNote' },
  { icon: <EditCalendarIcon />, name: 'Create Event', permissibleEntity: PermissionEntityEnum.Events, modal: 'CreateEvent' },
  { icon: <CreateNewFolderIcon />, name: 'Create Project', permissibleEntity: PermissionEntityEnum.Projects, modal: 'CreateProject', hideSpiderOnClick: true },
  { icon: <AddBoxIcon />, name: 'Create Task', permissibleEntity: PermissionEntityEnum.Tasks, modal: 'CreateTask' },
  { icon: <FlashOn />, name: 'Create Kick Off', permissibleEntity: PermissionEntityEnum.ProjectKickOff, modal: 'KickOff', hideSpiderOnClick: true },
  { icon: <VideoChatIcon />, name: 'New Meeting', permissibleEntity: PermissionEntityEnum.Projects, modal: 'Meeting' },
];

const ShortcutSpider: React.FC = () => {
  const { canPerformAction, checkRequestExists, requestPermission } = useContext(PermissionContext);
  const openedModal = useReactiveVar(setOpenedModals);
  const [open, setOpen] = useState(false);

  const handleSpeedDialActionClick = (action: SpeedDialActionItem) => {
    // if (action.hideSpiderOnClick) {
    //   const spider = document.getElementById('action-spider');
    //   if (spider) spider.style.visibility = "hidden";
    // }
    setOpenedModals([...openedModal, action.modal]);
    // globalMinimizedModals(filter(minimizedModals, tab => tab.type !== "<type>"));
    setOpen(false);
  };

  return (
    <React.Fragment>
      <SpeedDial
        ariaLabel="overwatch actions shortcut"
        // id="action-spider"
        direction='left'
        sx={{ position: 'absolute', bottom: 16, right: 16, zIndex: 1400 }}
        icon={<SpeedDialIcon />}
        open={open}
        // only close on explicit toggle / Esc — NOT on mouseLeave or blur, which is what
        // collapsed the dial when users drifted off it
        onClose={(_event, reason) => {
          if (reason === 'toggle' || reason === 'escapeKeyDown') setOpen(false);
        }}
        onOpen={(_event, reason) => {
          if (reason === 'toggle') setOpen(true);
        }}
        // workaround for MUI controlled-SpeedDial bug (onOpen immediately followed by onClose
        // on first tap): drive the toggle from the Fab's own click instead of the root
        FabProps={{
          onClick: () => setOpen((prev) => !prev),
        }}
      >
        {map((speedDialActions), (action) => {
          // check for permission if a permissible entity is defined, it its not defined that it doesn't need a permission check
          const hasPermission = action.permissibleEntity ? canPerformAction(PermissionActionEnum.Create, action.permissibleEntity) : true;

          return (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={
                !hasPermission && action.permissibleEntity ?
                  <RequestAccessBlock
                    requiredPermission={{
                      action: PermissionActionEnum.Create,
                      entity: action.permissibleEntity
                    }}
                    checkRequestExists={checkRequestExists}
                    onRequest={requestPermission}
                  /> :
                  action.name
              }
              onClick={() => {
                if (!hasPermission) return;
                return handleSpeedDialActionClick(action);
              }}
            />
          );
        })}
      </SpeedDial>
      <ControlledModals />
    </React.Fragment>
  );
};

export default ShortcutSpider;
